import { useTranslation } from '../../core/hooks/use-translation';
import { DocumentIcon, ShieldIcon } from './policy-icons';
import type { PolicyTab } from './policy-tab';

interface PolicyTabsProps {
    current: PolicyTab;
    /** Ids of the two panels, so each tab points at the document it shows. */
    panelIds: Record<PolicyTab, string>;
    onChange: (tab: PolicyTab) => void;
}

/**
 * The sticky bar that swaps between the two documents.
 *
 * A real tab list — `role="tablist"` with `aria-controls` on each tab and `aria-selected` on the
 * chosen one — where the live page has two buttons that toggle a class. Both panels stay in the DOM
 * either way; this is what tells a screen reader that the hidden one is the other half of the same
 * control rather than a section that vanished.
 */
export function PolicyTabs({ current, panelIds, onChange }: PolicyTabsProps) {
    const { t } = useTranslation();

    return (
        <div className="tabs-wrapper">
            <div className="tabs-inner" role="tablist" aria-label={t('policy.page.title')}>
                <button
                    type="button"
                    role="tab"
                    aria-selected={current === 'privacy'}
                    aria-controls={panelIds.privacy}
                    className={current === 'privacy' ? 'tab-btn active' : 'tab-btn'}
                    onClick={() => onChange('privacy')}
                >
                    <ShieldIcon />
                    <span>{t('policy.tab.privacy')}</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={current === 'terms'}
                    aria-controls={panelIds.terms}
                    className={current === 'terms' ? 'tab-btn active' : 'tab-btn'}
                    onClick={() => onChange('terms')}
                >
                    <DocumentIcon />
                    <span>{t('policy.tab.terms')}</span>
                </button>
            </div>
        </div>
    );
}
